import { App, AppState } from "@capacitor/app";
import { Preferences } from "@capacitor/preferences";
import { useSession } from "@src/app/store/helperHooks";
import { isEmpty } from "lodash";
import { FC, useEffect, useState } from "react";
import { useDispatch } from "react-redux";

import { AlwaysAllowLocationPrimerModal } from "./alwaysAllowLPermissionPrimerModal/alwaysAllowLPermissionPrimerModal";
import { updateAgentData } from "./api";
import { UrgentShiftLocalStorage } from "./constants";
import {
  checkIsFullLocationPermissionRequired,
  getCurrentLocationStatus,
  getPreviousLocationPermissionStatus,
  handleException,
  requestFullLocationAccess,
} from "./utils";
import { updateAgent } from "../../store/session";

const UrgentShiftPermissionContainer: FC<{}> = () => {
  const [showingFullPermissionModal, setShowingFullPermissionModal] = useState<boolean>(false);
  const { agent } = useSession();
  const [appIsActive, setAppIsActive] = useState(false);

  const dispatch = useDispatch();

  const showFullPermissionModal = () => {
    setShowingFullPermissionModal(true);
  };

  const updateLocationServiceStatus = async () => {
    const data = await getCurrentLocationStatus();

    const fields = ["isLocationServicesEnabled", "locationServicesPermission", "preciseLocation"];
    const updateInfo = {};

    let dataUpdated = false;
    const previousPermission = await getPreviousLocationPermissionStatus(agent);

    fields.forEach((field) => {
      updateInfo[`mobile.${field}`] = data[field];

      dataUpdated = dataUpdated || data[field] !== previousPermission?.[field];
    });

    const requestFullLocationPermission = await checkIsFullLocationPermissionRequired(data, agent);

    if (dataUpdated && !isEmpty(updateInfo)) {
      handleUpdateAgent(agent?.userId ?? (previousPermission?.agentId as string), data, updateInfo);
    }

    if (requestFullLocationPermission) {
      showFullPermissionModal();
    } else {
      hideFullPermissionModal();
    }
  };

  const handleUpdateAgent = async (
    agentId: string,
    data: Record<string, unknown>,
    updateInfo: Record<string, unknown>
  ) => {
    await Preferences.set({
      key: UrgentShiftLocalStorage.LAST_LOCATION_PERMISSION_STATUS,
      value: JSON.stringify({ ...data, agentId: agent?.userId }),
    });

    await updateAgentData(agentId, updateInfo);
    dispatch(
      updateAgent({
        mobile: {
          ...agent?.mobile,
          ...data,
        },
      })
    );
  };

  const onStateChange = (state: AppState) => {
    setAppIsActive(state.isActive);
  };

  // Event listeners
  useEffect(() => {
    const appStateChangeListener = App.addListener("appStateChange", onStateChange);

    handleException(updateLocationServiceStatus)();
    return () => {
      appStateChangeListener.remove();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (appIsActive) {
      handleException(updateLocationServiceStatus)();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appIsActive]);

  const hideFullPermissionModal = () => {
    setShowingFullPermissionModal(false);
  };

  const onGoToLocationSettingClicked = () => {
    hideFullPermissionModal();
    requestFullLocationAccess();
  };

  return (
    <AlwaysAllowLocationPrimerModal
      isOpen={showingFullPermissionModal}
      onGoToLocationSettingClicked={onGoToLocationSettingClicked}
    />
  );
};

export default UrgentShiftPermissionContainer;
